import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { agricultureInfo } from '../data/mock';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  Wheat, TrendingUp, TrendingDown, Minus, Cloud, Droplets, Thermometer, Sun
} from 'lucide-react';

const AgriculturePage = () => {
  const { getText, language } = useLanguage();

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <TrendingUp className="h-4 w-4 text-emerald-500" />;
      case 'down': return <TrendingDown className="h-4 w-4 text-red-500" />;
      default: return <Minus className="h-4 w-4 text-gray-400" />;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'up': return 'text-emerald-600 bg-emerald-50';
      case 'down': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Hero */}
      <section className="bg-gradient-to-r from-amber-500 to-orange-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {language === 'pa' ? 'ਖੇਤੀਬਾੜੀ ਜਾਣਕਾਰੀ' : 'Agriculture Info'}
            </h1>
            <p className="text-amber-100">
              {language === 'pa' ? 'ਫ਼ਸਲਾਂ ਦੇ ਭਾਅ ਅਤੇ ਮੌਸਮ ਦੀ ਜਾਣਕਾਰੀ' : 'Crop prices and weather information'}
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Weather Card */}
          <Card className="lg:col-span-1 border-0 shadow-lg bg-gradient-to-br from-blue-500 to-cyan-500 text-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Cloud className="h-5 w-5" />
                {language === 'pa' ? 'ਮੌਸਮ' : 'Weather'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center mb-6">
                <Sun className="h-16 w-16 mx-auto mb-2 text-yellow-300" />
                <p className="text-5xl font-bold mb-1">{agricultureInfo.weather.temperature}</p>
                <p className="text-blue-100">{getText(agricultureInfo.weather.condition)}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-lg p-3 text-center">
                  <Droplets className="h-5 w-5 mx-auto mb-1 text-blue-200" />
                  <p className="text-lg font-semibold">{agricultureInfo.weather.humidity}</p>
                  <p className="text-xs text-blue-100">{language === 'pa' ? 'ਨਮੀ' : 'Humidity'}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 text-center">
                  <Thermometer className="h-5 w-5 mx-auto mb-1 text-red-300" />
                  <p className="text-lg font-semibold">32°C</p>
                  <p className="text-xs text-blue-100">{language === 'pa' ? 'ਤਾਪਮਾਨ' : 'Feels like'}</p>
                </div>
              </div>
              
              <div className="mt-6 p-3 bg-white/10 rounded-lg">
                <p className="text-sm text-center">
                  {getText(agricultureInfo.weather.forecast)}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Crop Prices */}
          <Card className="lg:col-span-2 border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wheat className="h-5 w-5 text-amber-500" />
                {language === 'pa' ? 'ਫ਼ਸਲਾਂ ਦੇ ਭਾਅ' : 'Crop Prices (MSP)'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                {agricultureInfo.cropPrices.map((item, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                        <Wheat className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{getText(item.crop)}</p>
                        <p className="text-lg font-bold text-amber-600">{item.price}</p>
                      </div>
                    </div>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${getTrendColor(item.trend)}`}>
                      {getTrendIcon(item.trend)}
                      <span className="text-xs font-medium capitalize">{item.trend}</span>
                    </div>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-gray-400 mt-4 text-center">
                {language === 'pa' ? 'ਭਾਅ ਸਰਕਾਰ ਦੁਆਰਾ ਨਿਰਧਾਰਤ MSP ਅਨੁਸਾਰ ਹਨ' : 'Prices are based on government MSP rates'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Major Crops */}
        <Card className="mt-8 border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wheat className="h-5 w-5 text-amber-500" />
              {language === 'pa' ? 'ਮੁੱਖ ਫ਼ਸਲਾਂ' : 'Major Crops'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {agricultureInfo.majorCrops.map((crop, index) => (
                <div 
                  key={index}
                  className="text-center p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl hover:from-amber-100 hover:to-orange-100 transition-colors"
                >
                  <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-3">
                    <Wheat className="h-7 w-7 text-amber-600" />
                  </div>
                  <p className="font-semibold text-gray-900">{getText(crop)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Agriculture Tips */}
        <Card className="mt-8 border-0 shadow-lg">
          <CardHeader>
            <CardTitle>{language === 'pa' ? 'ਖੇਤੀ ਸੁਝਾਅ' : 'Agriculture Tips'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-emerald-50 rounded-xl">
                <h4 className="font-semibold text-emerald-800 mb-2">
                  {language === 'pa' ? 'ਸਿੰਚਾਈ ਦਾ ਸਮਾਂ' : 'Irrigation Tips'}
                </h4>
                <p className="text-sm text-emerald-600">
                  {language === 'pa' 
                    ? 'ਸਵੇਰੇ ਜਾਂ ਸ਼ਾਮ ਨੂੰ ਪਾਣੀ ਦਿਓ ਪਾਣੀ ਬਚਾਉਣ ਲਈ'
                    : 'Water plants in early morning or evening to conserve water'}
                </p>
              </div>
              <div className="p-4 bg-blue-50 rounded-xl">
                <h4 className="font-semibold text-blue-800 mb-2">
                  {language === 'pa' ? 'ਮੌਸਮ ਚੇਤਾਵਨੀ' : 'Weather Alert'}
                </h4>
                <p className="text-sm text-blue-600">
                  {language === 'pa'
                    ? 'ਅਗਲੇ ਦੋ ਦਿਨਾਂ ਵਿੱਚ ਮੀਂਹ ਦੀ ਸੰਭਾਵਨਾ ਹੈ'
                    : 'Rain expected in next 2 days, plan accordingly'}
                </p>
              </div>
              <div className="p-4 bg-amber-50 rounded-xl">
                <h4 className="font-semibold text-amber-800 mb-2">
                  {language === 'pa' ? 'ਮੰਡੀ ਭਾਅ' : 'Market Update'}
                </h4>
                <p className="text-sm text-amber-600">
                  {language === 'pa'
                    ? 'ਕਣਕ ਦੇ ਭਾਅ ਵਿੱਚ ਵਾਧਾ ਹੋਇਆ ਹੈ'
                    : 'Wheat prices have increased this season'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AgriculturePage;
